/*
 * Copyright (c) 2021. American Express Company
 */

// third-party
import React, { Component, ReactComponentElement } from 'react';
import { Dialog } from '@mui/material';
import ClickAwayListener from '@mui/base/ClickAwayListener';

// models and services
import { DraggableListItemModel } from 'src/types/view-models/draggable-list.view.model';
import { DrawButtonViewModel } from 'src/types/view-models/draw-button.view.model';
import { FundingSourceModel } from 'src/types/funding-source.model';
import LiteralsService from '../../services/literals/LiteralsService';

// components
import { IconBank, ButtonPrimary, ButtonSecondary } from '@americanexpress/dls-react';
import DraggableList from 'src/bw/draggable-list/DraggableList';
import Typography from '../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import { PRODUCT_NAME } from 'src/utils/constants';
import OptionsBox from '../../components/options-box/OptionsBox';

// styles
import optionsBoxStyles from '../../components/options-box/OptionsBox.module.scss';
import styles from './DrawButton.module.scss';

export default class DrawButton extends Component<DrawButtonViewModel> {
    public override state = {
        anchorEl: null as Element,
        items: [] as DraggableListItemModel[]
    };

    constructor(props: any) {
        super(props);
        this.state = {
            anchorEl: null as Element,
            items: this.getItemsFromCard()
        };
    }

    public override render(): ReactComponentElement<any> {
        const summary: JSX.Element = this.props.card.investmentIds && (
            <Typography variant={TypographyVariants.SUBTEXT} className={optionsBoxStyles.bodyText}>
                {PRODUCT_NAME} is drawing from{' '}
                <b>
                    {this.props.card.investmentIds.length} account
                    {this.props.card.investmentIds.length !== 1 ? 's' : ''}
                </b>{' '}
                to pay for these costs.
            </Typography>
        );
        return (
            <OptionsBox headerText="Adjust Payment Sources" icon={<IconBank />}>
                <Dialog
                    open={!!this.state.anchorEl}
                    PaperProps={{ className: styles.paper }}
                    className={styles.drawButtonPopoverContainer}
                >
                    <ClickAwayListener onClickAway={() => this.setState({ anchorEl: null })}>
                        <div className={styles.mainContent}>
                            <div>
                                <Typography variant={TypographyVariants.HEADING_S}>
                                    {LiteralsService.getLiteral('DrawButton.title')}
                                </Typography>
                            </div>
                            <div>
                                <Typography variant={TypographyVariants.SUBTEXT}>
                                    {LiteralsService.getLiteral('DrawButton.subtitle')}
                                </Typography>
                            </div>
                            <div>
                                <DraggableList
                                    {...{
                                        items: this.state.items,
                                        callback: this.onUpdate.bind(this),
                                        height: 289
                                    }}
                                />
                            </div>
                            <div className={styles.buttonContainer}>
                                <div>
                                    <ButtonPrimary size="sm" onClick={this.onSubmit.bind(this)}>
                                        UPDATE
                                    </ButtonPrimary>
                                </div>
                                <div>
                                    <ButtonSecondary size="sm" onClick={this.onCancel.bind(this)}>
                                        CANCEL
                                    </ButtonSecondary>
                                </div>
                            </div>
                        </div>
                    </ClickAwayListener>
                </Dialog>
                {summary}
                <ButtonSecondary onClick={(event: any) => this.setState({ anchorEl: event.currentTarget })} size="sm">
                    Adjust Payment Sources
                </ButtonSecondary>
            </OptionsBox>
        );
    }

    private getItemsFromCard(): DraggableListItemModel[] {
        if (!this.props.card.fundingSources) return [];
        return this.props.card.fundingSources.map((source: FundingSourceModel) => ({
            checked: this.props.card.investmentIds.findIndex((id: number) => id === source.investmentId) > -1,
            id: source.investmentId,
            subtext:
                source.usedInOtherCards > 0
                    ? `Used in ${source.usedInOtherCards} other event${source.usedInOtherCards > 1 ? 's' : ''}`
                    : undefined,
            text: source.name
        }));
    }

    private onCancel(): void {
        if (this.props.callback) this.props.callback(this.props.card.investmentIds);
        this.setState({ anchorEl: null });
    }

    private onSubmit(): void {
        if (this.props.callback)
            this.props.callback(
                this.state.items
                    .filter((item: DraggableListItemModel) => item.checked)
                    .map((item: DraggableListItemModel) => item.id)
            );
        this.setState({ anchorEl: null });
    }

    private onUpdate(items: DraggableListItemModel[]): void {
        this.setState({ items });
    }
}
